import { supabase, isSupabaseConfigured } from './supabase.js';
import seedData from '../data/seed.json';

/**
 * Fetch all reports with optional filters.
 */
export async function fetchReports(filters = {}) {
  // Fallback to seed data when Supabase is not configured
  if (!isSupabaseConfigured()) {
    return filterSeedData(seedData, filters);
  }

  let query = supabase
    .from('reports')
    .select('*')
    .order('created_at', { ascending: false });

  if (filters.categories && filters.categories.length > 0) {
    query = query.in('category', filters.categories);
  }

  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  if (filters.keyword) {
    query = query.or(
      `title.ilike.%${filters.keyword}%,description.ilike.%${filters.keyword}%`
    );
  }

  if (filters.dateFrom) {
    query = query.gte('created_at', filters.dateFrom);
  }

  if (filters.dateTo) {
    const dateTo = filters.dateTo.includes('T') ? filters.dateTo : `${filters.dateTo}T23:59:59`;
    query = query.lte('created_at', dateTo);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('Supabase fetch failed, using seed data:', error.message);
    return filterSeedData(seedData, filters);
  }

  return data && data.length > 0 ? data : filterSeedData(seedData, filters);
}

/**
 * Create a new report.
 */
export async function createReport(reportData) {
  if (!isSupabaseConfigured()) {
    // Simulate creation with seed data
    const newReport = {
      id: crypto.randomUUID(),
      ...reportData,
      status: 'open',
      upvotes: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    seedData.push(newReport);
    return newReport;
  }

  try {
    const { data, error } = await supabase
      .from('reports')
      .insert([reportData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.warn('Supabase insert failed, saving locally:', err.message);
    // Fallback: save to local seed data
    const newReport = {
      id: crypto.randomUUID(),
      ...reportData,
      status: 'open',
      upvotes: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    seedData.push(newReport);
    return newReport;
  }
}

/**
 * Fetch a single report by ID.
 */
export async function fetchReportById(id) {
  if (!isSupabaseConfigured()) {
    return seedData.find((r) => r.id === id) || null;
  }

  const { data, error } = await supabase
    .from('reports')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching report:', error);
    throw error;
  }

  return data;
}

/**
 * Upvote a report.
 */
export async function upvoteReport(id) {
  if (!isSupabaseConfigured()) {
    const report = seedData.find((r) => r.id === id);
    if (report) report.upvotes = (report.upvotes || 0) + 1;
    return report;
  }

  // Fetch current upvotes first
  const { data: current } = await supabase
    .from('reports')
    .select('upvotes')
    .eq('id', id)
    .single();

  const { data, error } = await supabase
    .from('reports')
    .update({ upvotes: (current?.upvotes || 0) + 1 })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error upvoting report:', error);
    throw error;
  }

  return data;
}

/**
 * Filter seed data locally (fallback mode).
 */
function filterSeedData(data, filters) {
  let filtered = [...data];

  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter((r) => filters.categories.includes(r.category));
  }

  if (filters.status) {
    filtered = filtered.filter((r) => r.status === filters.status);
  }

  if (filters.keyword) {
    const kw = filters.keyword.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.title.toLowerCase().includes(kw) ||
        r.description.toLowerCase().includes(kw)
    );
  }

  if (filters.dateFrom) {
    filtered = filtered.filter((r) => r.created_at >= filters.dateFrom);
  }

  if (filters.dateTo) {
    const dateTo = filters.dateTo.includes('T') ? filters.dateTo : `${filters.dateTo}T23:59:59`;
    filtered = filtered.filter((r) => r.created_at <= dateTo);
  }

  return filtered.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
}
